import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { Divider, Stack, Box, Typography } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalCustomActionBar from 'components/generic/modals/form-modal/FormModalCustomActionBar'
import DataGrid from 'components/generic/datagrid'
import Loading from 'components/generic/Loading'
import { useGetInboundTicketsQuery, useMergeInboundTicketsMutation } from 'services/inboundTickets'
import { setSnackBar } from 'state/ui'
import { useNavigate } from 'react-router-dom'
import { CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { getActionButtons, getColumns, getRows, useStyles } from './helperMergeTickets'
import { TICKET_STATUSES } from 'utils/constants'

const MergeTicketsModal = ({ onFormStateChange, onHideModal }) => {
  const { ticketId, billId, ticketNumber, customerId } = useSelector(state => state.ui.formModalProps)
  const [selected, setSelected] = useState([])
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { data, isLoading } = useGetInboundTicketsQuery(
    {
      filter: JSON.stringify({
        customerId,
        statuses: [
          TICKET_STATUSES.INCOMPLETE.id,
          TICKET_STATUSES.PRICE_COMPLETE.id,
          TICKET_STATUSES.APPROVED.id,
          TICKET_STATUSES.SCALE_COMPLETE.id,
          TICKET_STATUSES.TRUCK_IN_YARD.id,
        ],
      }),
    },
    { skip: !customerId || !ticketNumber },
  )

  const [mergeInboundTickets, { isLoading: isLoadingMergeInboundTickets }] = useMergeInboundTicketsMutation()

  const handleSuccess = message => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: message,
      }),
    )
    trackEvent(cz.MERGE_TICKETS)
    onHideModal(navigate)
  }

  const handleError = error => {
    const errorMsg = error?.data?.error || 'There was an error merging tickets'
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
    trackEvent({ eventName: cz.MERGE_TICKETS, errorMsg })
  }

  const onSubmit = async () => {
    if (selected.length === 0) {
      dispatch(
        setSnackBar({
          snackbarType: 'error',
          snackbarMessage: 'No tickets selected for merge. Please select at least one ticket to merge.',
        }),
      )
      return
    }
    await mergeInboundTickets({
      id: ticketId,
      billId,
      mergeIbtsRequest: selected,
    })
      .unwrap()
      .then(() => {
        handleSuccess('Tickets successfully merged')
      })
      .catch(error => handleError(error))
  }

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  if (isLoadingMergeInboundTickets) {
    return <Loading localized />
  }

  return (
    <Stack height="100vh">
      <FormModalHeading title="Select Tickets to Merge" backButtonPosition="right" onBack={() => onHideModal()} />
      <Divider />
      <Typography variant="body1" mt={3} ml={4}>
        {`Any selected tickets will be merged with Inbound Ticket #${ticketNumber} and voided.`}
      </Typography>
      <Box flexGrow={1} overflow="auto">
        <DataGrid
          flexGrow={1}
          loading={isLoading}
          columns={getColumns()}
          rows={getRows(data, ticketId)}
          height="auto"
          checkboxSelection
          className={classes.root}
          selectionModel={selected}
          onSelectionModelChange={newSelectionModel => {
            setSelected(newSelectionModel)
          }}
        />
      </Box>
      <FormModalCustomActionBar buttonConfigs={getActionButtons(onSubmit, onHideModal)} justify="flex-start" />
    </Stack>
  )
}

MergeTicketsModal.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

MergeTicketsModal.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default MergeTicketsModal
